import { useEffect, useState } from "react";
import { MessageCircle, Save, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELDS = [
    { key: "business_name", label: "Legal business name" },
    { key: "business_trading_as", label: "Trading as" },
    { key: "business_owner", label: "Owner name" },
    { key: "business_address", label: "Address", type: "textarea" },
    { key: "business_phone", label: "Phone" },
    { key: "business_email", label: "Email" },
    { key: "business_website", label: "Website" },
    { key: "business_type", label: "Business type (Sole trader / Ltd)" },
    { key: "vat_number", label: "VAT number (if registered)" },
    { key: "bank_account_name", label: "Bank account name", section: "bank" },
    { key: "bank_sort_code", label: "Sort code (e.g. 12-34-56)", section: "bank" },
    { key: "bank_account_number", label: "Account number", section: "bank" },
    { key: "bank_name", label: "Bank name", section: "bank" },
    { key: "invoice_payment_terms", label: "Invoice payment terms", section: "inv" },
    { key: "invoice_number_prefix", label: "Invoice number prefix (default DH)", section: "inv" },
];

const Settings = () => {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () =>
        api.get("/settings/business").then((r) => setS(r.data)).catch(() => toast.error("Failed to load"));
    useEffect(load, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...s, vat_registered: !!s.vat_registered };
            await api.put("/settings/business", payload);
            toast.success("Settings saved");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!s) return <p className="text-neutral-500">Loading…</p>;

    const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const vcardUrl = `${API_BASE}/api/business/vcard`;
    const shareText = `Hi, I'm ${s.business_owner || "Hayden"} at ${s.business_trading_as || s.business_name || "Dragon Heat"}. Save my details: ${vcardUrl}`;
    const shareViaWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, "_blank", "noopener");
    };
    const nativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: s.business_trading_as || "Dragon Heat",
                    text: shareText,
                    url: vcardUrl,
                });
            } catch {
                /* user cancelled */
            }
        } else {
            navigator.clipboard.writeText(shareText).then(
                () => toast.success("Copied — paste to share"),
                () => toast.error("Copy failed"),
            );
        }
    };

    const grouped = {
        biz: FIELDS.filter((f) => !f.section),
        bank: FIELDS.filter((f) => f.section === "bank"),
        inv: FIELDS.filter((f) => f.section === "inv"),
    };

    return (
        <form onSubmit={save} className="space-y-8" data-testid="settings-page">
            <div>
                <p className="text-label" style={{ color: "var(--dh-muted)" }}>Configure</p>
                <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
                    Business &amp; invoice settings
                </h2>
                <p className="text-sm text-neutral-600 mt-2 max-w-xl">
                    These details are baked into every invoice PDF and email. Change
                    them once here and every new invoice picks them up. Existing
                    invoices keep the snapshot they were sent with — untouched.
                </p>
            </div>

            <section className="dh-card p-5 sm:p-6 space-y-4" data-testid="settings-business">
                <h3 className="font-display font-black text-xl uppercase">Business details</h3>
                {grouped.biz.map((f) => (
                    <div key={f.key}>
                        <label className="text-label block mb-1">{f.label}</label>
                        {f.type === "textarea" ? (
                            <textarea
                                rows={2}
                                className="dh-input"
                                value={s[f.key] || ""}
                                data-testid={`field-${f.key}`}
                                onChange={(e) => set(f.key, e.target.value)}
                            />
                        ) : (
                            <input
                                className="dh-input"
                                value={s[f.key] || ""}
                                data-testid={`field-${f.key}`}
                                onChange={(e) => set(f.key, e.target.value)}
                            />
                        )}
                    </div>
                ))}
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!s.vat_registered}
                        data-testid="field-vat_registered"
                        onChange={(e) => set("vat_registered", e.target.checked)}
                    />
                    <span>VAT registered — show VAT number and 20% VAT on invoices</span>
                </label>
            </section>

            <section className="dh-card p-5 sm:p-6 space-y-4" data-testid="settings-bank">
                <h3 className="font-display font-black text-xl uppercase">Bank details</h3>
                <p className="text-xs text-neutral-500">
                    These print on every invoice PDF + email under &ldquo;Pay by bank
                    transfer&rdquo;. Take a moment to double-check them.
                </p>
                {grouped.bank.map((f) => (
                    <div key={f.key}>
                        <label className="text-label block mb-1">{f.label}</label>
                        <input
                            className="dh-input"
                            value={s[f.key] || ""}
                            data-testid={`field-${f.key}`}
                            onChange={(e) => set(f.key, e.target.value)}
                        />
                    </div>
                ))}
            </section>

            <section className="dh-card p-5 sm:p-6 space-y-4" data-testid="settings-invoice">
                <h3 className="font-display font-black text-xl uppercase">Invoice defaults</h3>
                {grouped.inv.map((f) => (
                    <div key={f.key}>
                        <label className="text-label block mb-1">{f.label}</label>
                        <input
                            className="dh-input"
                            value={s[f.key] || ""}
                            data-testid={`field-${f.key}`}
                            onChange={(e) => set(f.key, e.target.value)}
                        />
                    </div>
                ))}
            </section>

            <section
                className="dh-card p-5 sm:p-6 space-y-3"
                data-testid="settings-share"
            >
                <h3 className="font-display font-black text-xl uppercase">
                    Share your details
                </h3>
                <p className="text-sm text-neutral-600 max-w-xl">
                    Text the customer a link that opens as a contact on their phone —
                    they save you in one tap. Great for meeting people at the door,
                    on referrals, or handing out at trade counters.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={shareViaWhatsApp}
                        className="dh-btn"
                        data-testid="settings-share-whatsapp"
                    >
                        <MessageCircle size={16} /> Share via WhatsApp
                    </button>
                    <button
                        type="button"
                        onClick={nativeShare}
                        className="dh-btn bg-white"
                        data-variant="ghost"
                        data-testid="settings-share-native"
                    >
                        <Share2 size={16} /> Share…
                    </button>
                    <a
                        href={vcardUrl}
                        className="dh-btn bg-white"
                        data-variant="ghost"
                        data-testid="settings-share-vcard"
                    >
                        Download vCard
                    </a>
                </div>
            </section>

            <div className="flex justify-end">
                <button className="dh-btn" data-testid="settings-save-btn" disabled={saving}>
                    <Save size={16} /> {saving ? "Saving…" : "Save settings"}
                </button>
            </div>
        </form>
    );
};

export default Settings;
